import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import axios from "axios";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase-config";
import { toast } from "react-toastify";
import { useAuthContext } from "./authContext";

interface Notification {
  id: number;
  title: string;
  body: string;
  type: string;
  status: string;
  createdAt: string;
  supervisionInvite?: {
    id: number;
    projectId: number;
    status: string;
  };
}

interface NotificationsContextType {
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  markAllAsRead: () => void;
  markAsRead: (id: number) => void;
  fetchNotifications: () => Promise<void>;
  markSupervisionInviteNotificationAsRead: (inviteId: number) => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const { isAuthenticated } = useAuthContext();

  const fetchNotifications = useCallback(async () => {
    try {
      if (!isAuthenticated) return;

      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await axios.get(
        `${process.env.REACT_APP_URL}/notifications?page=1&limit=50`,
        {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        }
      );

      if (res.data?.notifications) {
        setNotifications(res.data.notifications);
      }
    } catch (error) {}
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const addNotification = (notification: Notification) => {
    setNotifications((prev) => [notification, ...prev]);
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const eligibleIds = notifications
        .filter((n) => {
          if (n.type !== "SUPERVISION_INVITE") return true;
          return n.supervisionInvite?.status !== "PENDING";
        })
        .filter((n) => n.status !== "RECEIVED")
        .map((n) => n.id);

      if (eligibleIds.length === 0) return;

      await axios.put(
        `${process.env.REACT_APP_URL}/notifications/received`,
        { notificationIds: eligibleIds },
        {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        }
      );

      setNotifications((prev) =>
        prev.map((n) =>
          eligibleIds.includes(n.id) ? { ...n, status: "RECEIVED" } : n
        )
      );
    } catch (error) {}
  };

  const markAsRead = async (id: number) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await axios.put(
        `${process.env.REACT_APP_URL}/notifications/received`,
        { notificationIds: [id] },
        {
          headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" },
        }
      );

      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: "RECEIVED" } : n))
      );
    } catch (error) {}
  };

  const markSupervisionInviteNotificationAsRead = async (inviteId: number) => {
    const notificationToUpdate = notifications.find(
      (n) => n.supervisionInvite?.id === inviteId
    );
    if (notificationToUpdate) {
      await markAsRead(notificationToUpdate.id);
    }
  };

  useEffect(() => {
    const unsubscribe = onMessage(messaging, async (payload) => {
      const { notification, data } = payload;

      if (notification) {
        const newNotification: Notification = {
          id: data?.id ? Number(data.id) : Date.now(),
          title: notification.title || "No Title",
          body: notification.body || "No Body",
          type: data?.type || "GENERAL",
          status: "SENT",
          createdAt: data?.createdAt || new Date().toISOString(),
        };

        setNotifications((prev) => [newNotification, ...prev]);

        await fetchNotifications();

        toast.info(`${notification.title}: ${notification.body}`, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [fetchNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        addNotification,
        markAllAsRead,
        markAsRead,
        fetchNotifications,
        markSupervisionInviteNotificationAsRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotificationsContext must be used within NotificationsProvider"
    );
  }
  return context;
};
